<template>
    <side-modal ref="modal" hide-footer hide-header close-button="outset">
        <div v-if="!isLoading" class="container-fluid" style="height: 100%;">
            <div class="row" style="height: 100%;">
                <div class="col-4" :style="'padding-top: 2rem; padding-bottom: 1rem; border-right: 1px solid #ddd; height: ' + (isEditable ? 'calc(100% - 53px)' : '100%') + '; overflow-y: auto;'">

                    <!-- Contact Header !-->
                    <contact-header-vertical
                        :contact="lead.contact"
                        :should-show-post-code="shouldShowPostCode"
                    ></contact-header-vertical>

                    <!-- Pipeline / Stage Selector !-->
                    <existing-lead-pipeline-stage-control
                        :stage-id="lead.stage ? lead.stage.id : undefined"
                        :type="lead.type"
                        @input="updateStage"
                        :disabled="!isEditable"
                    ></existing-lead-pipeline-stage-control>

                    <!-- Vehicles of Interest !-->
                    <div v-if="isSalesLead" style="margin-top: 0.8rem; margin-left: -8px; margin-right: -8px;">
                        <lead-sales-sub-pane
                            :value="{ vehiclesOfInterest, vehiclesOffered }"
                            :lead-id="lead.id"
                            hide-derivative
                            style="padding: 0 .5rem"
                            @added="vehicleAdded"
                            @removed="vehicleRemoved"
                            :is-busy="isDetailBusy"
                            :disabled="!isEditable"
                        ></lead-sales-sub-pane>
                    </div>
                    <div v-if="isBuyingLead" style="margin-top: 1.5rem; margin-left: -8px; margin-right: -8px;">
                        <lead-buying-sub-pane
                            :value="{ vehiclesOfInterest, vehiclesOffered }"
                            :lead="lead"
                            hide-derivative
                            style="padding: 0 .5rem"
                            @added="vehicleAdded"
                            @removed="vehicleRemoved"
                            :is-busy="isDetailBusy"
                            :disabled="!isEditable"
                        ></lead-buying-sub-pane>
                    </div>

                    <!-- Source !-->
                    <hr class="labeled" data-content="Source" style="margin-top: 1rem; margin-bottom: 0.5rem;">

                    <!-- Referral Source !-->
                    <referral-source-attribute-popover
                        v-model="lead.referral_source"
                        :save-callback="updateReferralSource"
                        :disabled="!isEditable"
                        highlight-on-hover
                    >
                        <span class="material-icons">directions</span>
                        <span>{{ lead.referral_source.name }}</span>
                    </referral-source-attribute-popover>

                    <!-- Referrer !-->
                    <div v-if="lead.web_tracking && lead.web_tracking.referrer" class="lead-meta-data-item">
                        <span class="material-icons">
                            subdirectory_arrow_right
                            <ui-tooltip>Referrer</ui-tooltip>
                        </span>
                        <div class="lead-meta-data-item-data">
                            <div style="font-size: 1rem;">
                                {{ hostname(lead.web_tracking.referrer) }}
                            </div>
                            <div>
                                <ui-icon-button
                                    color="primary"
                                    type="secondary"
                                    @click="openInNewTab(lead.web_tracking.referrer, [], {utm_campaign: 'Existing Lead', utm_source: 'dealerkit-dms'}, true)" size="mini" icon="open_in_new"></ui-icon-button>
                            </div>
                        </div>
                    </div>

                    <!-- Landing Page !-->
                    <div v-if="lead.web_tracking && lead.web_tracking.landing_page_url" class="lead-meta-data-item">
                        <span class="material-icons">
                            login
                            <ui-tooltip>Landing Page</ui-tooltip>
                        </span>
                        <div class="lead-meta-data-item-data">
                            <div>
                                {{ pathname(lead.web_tracking.landing_page_url) }}
                            </div>
                            <div>
                                <ui-icon-button
                                    color="primary"
                                    type="secondary"
                                    @click="openInNewTab(lead.web_tracking.landing_page_url, [], {utm_campaign: 'Existing Lead', utm_source: 'dealerkit-dms'}, true)" size="mini" icon="open_in_new"></ui-icon-button>
                            </div>
                        </div>
                    </div>

                    <!-- UTM Data !-->
                    <div v-if="hasUtmData" class="lead-meta-data-item">
                        <span class="material-icons">
                            query_stats
                            <ui-tooltip>UTM Tracking Data</ui-tooltip>
                        </span>
                        <div class="lead-meta-data-item-data">
                            <div>
                                <div class="utm-label-container">
                                    <span class="utm-label" v-for="key in utmKeys" v-if="lead.web_tracking[key] !== null && lead.web_tracking[key].trim().length > 0">
                                        {{ decodeUtm(lead.web_tracking[key]) }}
                                        <ui-tooltip>{{ titleCase(key) }}</ui-tooltip>
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Location & Owner !-->
                    <hr class="labeled" data-content="Details" style="margin-top: 1rem; margin-bottom: 0.5rem;">

                    <!-- Owner !-->
                    <user-attribute-popover
                        :save-callback="updateSalesPerson"
                        v-model="lead.sales_person"
                        :disabled="!isEditable"
                        highlight-on-hover
                    >
                        <span class="material-icons">face</span>
                        <span v-if="lead.sales_person">{{ lead.sales_person.name }}</span>
                        <span v-else>Unassigned</span>
                    </user-attribute-popover>

                    <!-- Location !-->
                    <location-attribute-popover
                        :save-callback="updateLocation"
                        v-model="lead.location"
                        :disabled="!isEditable"
                        highlight-on-hover
                        >
                        <span class="material-icons">map</span>
                        {{ lead.location.name }}
                    </location-attribute-popover>

                    <!-- Status !-->
                    <div :class="{ 'status-item': true, [lead.status ?? lead.status]: true }">
                        <span class="material-icons">traffic</span>
                        <lead-status-tag :lead="lead"></lead-status-tag>
                        <div class="lost-notes" v-if="lead.outcome && lead.status === 'lost'">
                            <strong>{{ lead.outcome.reason }}</strong>
                            {{ lead.outcome.notes }}
                        </div>
                    </div>

                    <!-- Type !-->
                    <div class="status-item">
                        <span class="material-icons">layers</span>
                        {{ lead.type | titleCase }} Lead
                    </div>

                </div>

                <div class="col" style="background: #fafafa; height: 100%; overflow-y: auto;">

                    <!-- Activity Stream !-->
                    <content-creation-card
                        ref="contentCreator"
                        :owners="owners"
                        style="margin-top:1.5rem;"
                        @created="() => { this.loadActivity(); this.triggerUpdate() }"
                    />
                    <stream
                        ref="stream"
                        :stream-retriever="streamRetriever"
                        @schedule="$refs.contentCreator.switchTab('activity')"
                        @updated="() => { this.triggerUpdate() }"
                    />

                </div>
            </div>

            <!-- Bottom Navigation !-->
            <div v-if="isEditable || isLost" class="col-4" style="background: #fafafa; position: absolute; bottom: 0; left: 1px; border-top: 1px solid #ddd; border-right: 1px solid #ddd;">
                <div v-if="isEditable" style="padding: .5rem 0; display: flex;">
                    <ui-button
                        @click="$refs.createDealModal.open()"
                        color="green"
                        icon="monetization_on"
                        style="flex-grow: 1; margin-right: .5rem"
                    >Won</ui-button>
                    <ui-button
                        @click="markLeadLost"
                        color="red"
                        icon="thumb_down"
                        style="flex-grow: 1; margin-right: .5rem"
                    >Lost</ui-button>
                    <ui-button
                        icon="delete"
                        class="lead-side-modal-delete-button"
                        @click="deleteLead"></ui-button>
                </div>

                <div v-else-if="isLost" style="padding: .5rem 0; display: flex;">
                    <ui-button
                        @click="rollbackLostLead"
                        color="green"
                        icon="history"
                        style="flex-grow: 1; margin-right: .5rem"
                    >Re-open</ui-button>
                </div>
            </div>
        </div>
        <div v-else style="height: 100%; display: flex;" class="align-items-center">
            <ui-progress-circular
                :stroke="2"
                type="indeterminate"
                style="margin:auto"
                :size="96">
            </ui-progress-circular>
        </div>

        <!-- Lost Lead Modal !-->
        <lost-lead-modal ref="lostModal" :lead="lead" v-if="lead" @lost="updateAndClose"></lost-lead-modal>

        <!-- Create Deal Modal !-->
        <lead-conversion-modal
            ref="createDealModal"
            :lead="lead"
            v-if="lead"
            @created="updateAndClose"
            @won="leadWon"
        ></lead-conversion-modal>
    </side-modal>
</template>

<script>
import Filters from "../../filters"
import SideModal from "../elements/SideModal";
import ContactHeaderVertical from "../elements/ContactHeaderVertical";
import {LeadDetailTypes, LeadResource} from "../../classes/Api/Lead";
import UserAttributePopover from "../forms/in-context/UserAttributePopover";
import Stream from "../elements/stream/Stream";
import ContentCreationCard from "../elements/stream/ContentCreationCard";
import LocationAttributePopover from "../forms/in-context/LocationAttributePopover";
import LeadSalesSubPane from "../forms/LeadSalesSubPane";
import LostLeadModal from "./LostLeadModal";
import LeadConversionModal from "./LeadConversionModal";
import LeadStatusTag from "./LeadStatusTag";
import ReferralSourceAttributePopover from "../forms/in-context/ReferralSourceAttributePopover";
import {ContactResource} from "../../classes/Api/Contact";
import {titleCase} from "../../filters/string";
import {pathname} from "../../filters/url/pathname";
import {openInNewTab} from "../../utils";
import {hostname} from "../../filters/url/hostname";
import {decodeUtm} from "../../filters/url/decode-utm";
import LeadBuyingSubPane from "../forms/LeadBuyingSubPane.vue";
import DealerKit from "../../api";
import {LeadType} from "../../../../api-clients/ts";
import ExistingLeadPipelineStageControl from "./ExistingLeadPipelineStageControl.vue";

const utmKeys = [
    'utm_source',
    'utm_medium',
    'utm_content',
    'utm_campaign',
    'utm_term',
]

export default {
    components: {
        ExistingLeadPipelineStageControl,
        LeadBuyingSubPane,
        ReferralSourceAttributePopover,
        LeadStatusTag,
        LeadConversionModal,
        LostLeadModal,
        LeadSalesSubPane,
        LocationAttributePopover,
        UserAttributePopover,
        ContactHeaderVertical,
        SideModal,
        Stream,
        ContentCreationCard
    },
    filters: Filters,
    data() {
        return {
            lead: null,
            activity: [],
            isLoading: true,
            isDetailBusy: false,
            utmKeys,
            vehiclesOfInterest: [],
            vehiclesOffered: [],
        }
    },
    methods: {
        decodeUtm,
        hostname,
        openInNewTab,
        pathname,
        titleCase,
        open(id) {
            this.isLoading = true

            this.$refs.modal.open()

            this.load(id)
        },
        load(id) {
            DealerKit.leads.getLead(id).then(r => {
                this.lead = r.data.data

                this.vehiclesOfInterest = this.lead.vehicles_of_interest
                this.vehiclesOffered = this.lead.appraisals

                this.isLoading = false

                this.$nextTick(() => {
                    this.loadActivity()
                })
            }).catch(handleApiError)
        },
        close() {
            this.$refs.modal.close()
        },
        updateAndClose() {
            this.triggerUpdate();

            this.close()
        },
        deleteLead() {
            if (confirm('Are you sure you want to PERMANENTLY delete this lead?')) {
                this.isLoading = true

                LeadResource.destroy(this.lead.id).then(() => {
                    this.$refs.modal.close()

                    this.triggerUpdate()
                }).catch(r => {
                    if (r.response && r.response.status === 403) {
                        alert('You do not have permission todo that.')
                    } else {
                        alert('There was a problem deleting the lead.')
                    }

                    this.isLoading = false
                })
            }
        },
        markLeadLost() {
            this.$refs.lostModal.open()
        },
        rollbackLostLead() {
            if (confirm('Are you sure you want to re-open the lead?')) {
                this.isLoading = true

                LeadResource.rollback(this.lead.id).then(() => {
                    this.load(this.lead.id)
                })
            }
        },
        vehicleAdded(e) {
            if (e.type === LeadDetailTypes.VEHICLE_OF_INTEREST) {
                DealerKit.leads.storeVehicleOfInterest(this.lead.id, {
                    vehicle_version_id: e.vehicle.id
                }).then(() => {
                    this.load(this.lead.id)
                    this.triggerUpdate()
                }).catch(handleApiError)
            } else if (e.type === LeadDetailTypes.VEHICLE_OFFERED) {
                const payload = {
                    condition: e.appraisal.condition,
                    service_history: e.appraisal.service_history,
                    outstanding_finance_amount: e.appraisal.outstanding_finance_amount ?? '',
                    outstanding_finance_provider: e.appraisal.outstanding_finance_provider ?? '',
                    appraised_value: e.appraisal.appraised_value,
                }

                let promise = undefined

                if (e.appraisal.id) {
                    promise = DealerKit.appraisals.patchVehicleAppraisal(e.appraisal.id, payload)
                } else {
                    promise = DealerKit.appraisals.storeVehicleAppraisal({
                        ...payload,
                        vehicle_id: e.appraisal.vehicle.id,
                        lead_id: this.lead.id
                    })
                }

                promise.finally(() => {
                    this.load(this.lead.id)
                    this.triggerUpdate()
                }).catch(handleApiError)
            }
        },
        vehicleRemoved(e) {
            if (e.type === LeadDetailTypes.VEHICLE_OF_INTEREST) {
                DealerKit.leads.destroyVehicleOfInterest(this.lead.id, e.vehicle.id).then(() => {
                    this.load(this.lead.id)
                    this.triggerUpdate()
                }).catch(handleApiError)
            } else if(e.type === LeadDetailTypes.VEHICLE_OFFERED) {
                DealerKit.appraisals.destroyVehicleAppraisal(e.appraisal.id).then(() => {
                    this.load(this.lead.id)
                    this.triggerUpdate()
                })
            }
        },
        updateSalesPerson(sales_person) {
            return new Promise((resolve, reject) => {
                DealerKit.leads.patchLead(this.lead.id, { sales_person_id: sales_person.id})
                    .then(response => {
                        this.lead = response.data.data
                        resolve(this.lead)
                        this.triggerUpdate()
                    })
                    .catch(e => { handleApiError(e, reject)})
            })
        },
        updateReferralSource(referralSource) {
            return new Promise((resolve, reject) => {
                DealerKit.leads.patchLead(this.lead.id, { referral_source_id: referralSource.id})
                    .then(response => {
                        this.lead = response.data.data
                        resolve(this.lead)
                        this.triggerUpdate()
                    })
                .catch(e => { handleApiError(e, reject)})
            })
        },
        updateLocation(location) {
            return new Promise((resolve, reject) => {
                DealerKit.leads.patchLead(this.lead.id, { location_id: location.id})
                    .then(response => {
                        this.lead = response.data.data
                        resolve(this.lead)
                        this.triggerUpdate()
                    })
                    .catch(e => { handleApiError(e, reject)})
            })
        },
        updateStage(stage) {
            return new Promise((resolve, reject) => {
                DealerKit.leads.patchLead(this.lead.id, { stage_id: stage.id})
                    .then(response => {
                        this.lead = response.data.data
                        resolve(this.lead)
                        this.triggerUpdate()
                    })
                    .catch(e => { handleApiError(e, reject)})
            })
        },
        triggerUpdate() {
            this.$emit('updated', this.lead)
        },
        streamRetriever() {
            return new Promise((resolve, reject) => {
                ContactResource.stream(this.lead.contact.id).then(response => {
                    resolve(response.data)
                }, (e) => { handleApiError(e, reject) });
            })
        },
        loadActivity() {
            if (this.$refs.stream) {
                this.$refs.stream.loadStream()
            }
        },
        leadWon() {
            this.triggerUpdate()

            this.close()
        }
    },
    computed: {
        shouldShowPostCode() {
            return window.marque.ui.tweaks.show_lead_post_codes && this.lead.contact.post_code
        },
        isBuyingLead() {
            return this.lead.type === LeadType.Buying
        },
        isSalesLead() {
            return this.lead.type === LeadType.Sales
        },
        hasUtmData() {
            if (!this.lead.web_tracking) {
                return false
            }

            return utmKeys.filter(key => this.lead.web_tracking[key]).length > 0
        },
        isEditable() {
          return !this.lead.outcome
        },
        isLost() {
            return this.lead.status === 'lost'
        },
        owners() {
            return [{
                type: 'contact',
                id: this.lead.contact.id,
                object: this.lead.contact,
            },{
                type: 'lead',
                id: this.lead.id,
                object: this.lead,
            }]
        }
    }
}
</script>

<style lang="scss" scoped>
    hr { font-size: 14px; }
    .attribute-popover {
        width: 100%;
        padding: .5rem 3.5rem .5rem .5rem;

        .material-icons {
            vertical-align: middle; margin-right: 1rem;
        }
    }

    .status-item {
        padding: 0.5rem 3.5rem 0.5rem 0.5rem;

        .material-icons { margin-right: 1rem; vertical-align: middle; }
    }

    .lost-notes {
        margin-top: 1rem;
        margin-left: 43px;
        padding: .5rem 1rem;
        background-color: rgb(255, 237, 237);
        border-radius: 5px;
        font-size: 14px;
        color: crimson;
    }

    .utm-label {
        font-size: 12px;
        display: inline-block;
        padding: 1px 6px;
        background: #efefef;
        color: rgb(153, 153, 153);
        margin-right: 3px;
        border-radius: 3px;
    }

    .lead-meta-data-item {
        padding: 0.5rem 0.5rem 0.5rem 0.5rem;
        .material-icons { margin-right: 1rem; vertical-align: middle; }
      .lead-meta-data-item-data {
            display: inline-flex;
            vertical-align: middle;
            max-width: 80%;
            font-size: 12px;
            align-items: center;

            & > div {
              &:first-child { flex: 1; padding-right: .5rem; }
            }
      }
    }
</style>
<style lang="scss">
    .lead-side-modal-delete-button {
        min-width: 3rem !important;
        .ui-button__icon { margin-right: 0; }
    }
</style>